<template>
  <div class="group relative flex flex-col h-full rounded-xl border bg-white dark:bg-[#1C2128] transition-shadow hover:shadow-md"
    :class="resume.is_default ? 'border-blue-200 dark:border-blue-800' : 'border-gray-100 dark:border-gray-700'">
    <RouterLink :to="`/cv_builder/${resume.id}`" data-turbo-prefetch="false" :aria-label="`Edit ${resume.name}`"
      class="block p-3 pb-0 rounded-t-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
      <div class="relative">
        <ResumeCardPreview :content="resume.content" :template="resume.template" :picture-url="resume.picture_url" />
        <!-- Written by AI: said once, on the card itself. The details (model, tokens, scores) are in the resume when it is opened. -->
        <span v-if="resume.ai_generated" class="absolute top-2 left-2 inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full text-[11px] font-semibold text-white bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500 shadow-md shadow-violet-500/30 ring-1 ring-white/40">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5Z"/></svg>
          AI generated
        </span>
      </div>
    </RouterLink>

    <div class="flex items-start gap-2 px-3.5 pt-3 pb-3.5 mt-auto">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ resume.name }}</h3>
          <span v-if="resume.is_default" class="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">Default</span>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{{ resume.headline || 'No headline yet' }}</p>
        <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 truncate">{{ templateLabel(resume.template) }} · Edited {{ edited }}</p>
      </div>

      <div ref="root" class="relative flex-shrink-0">
        <button type="button" :aria-label="`More actions for ${resume.name}`" aria-haspopup="menu" :aria-expanded="open" @click="open = !open"
          class="w-7 h-7 -mr-1 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 6.75a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm0 6.75a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm0 6.75a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"/></svg>
        </button>

        <ul v-if="open" role="menu" @keydown.esc="open = false"
          class="absolute right-0 bottom-full mb-1 z-30 w-48 py-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-lg">
          <li v-for="item in items" :key="item.action" role="none">
            <button type="button" role="menuitem" @click="choose(item.action)"
              class="w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-800"
              :class="item.danger ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-200'">{{ item.label }}</button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import ResumeCardPreview from './ResumeCardPreview.vue'
import { templateLabel } from './templates.js'

const props = defineProps({
  resume: { type: Object, required: true },
})
const emit = defineEmits(['edit-details', 'make-default', 'duplicate', 'delete'])

const open = ref(false)
const root = ref(null)

const items = computed(() => [
  props.resume.is_default ? null : { action: 'make-default', label: 'Make default' },
  { action: 'duplicate', label: 'Duplicate' },
  { action: 'edit-details', label: 'Rename or change template' },
  { action: 'delete', label: 'Delete', danger: true },
].filter(Boolean))

function choose(action) {
  open.value = false
  emit(action, props.resume)
}

// "3 days ago" style, from the last edit.
const UNITS = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]]
const edited = computed(() => {
  const seconds = Math.round((new Date(props.resume.updated_at) - Date.now()) / 1000)
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  const [unit, size] = UNITS.find(([, size]) => Math.abs(seconds) >= size) || ['minute', 60]
  return Math.abs(seconds) < 60 ? 'just now' : formatter.format(Math.round(seconds / size), unit)
})

// A click anywhere else closes the menu.
function onOutside(event) {
  if (root.value && !root.value.contains(event.target)) open.value = false
}
watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('pointerdown', onOutside)
  else document.removeEventListener('pointerdown', onOutside)
})
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutside))
</script>
